import { BigNumber } from '@0x/utils';
import React from 'react';
import { connect } from 'react-redux';

import {
    ETH_DECIMALS,
    STEP_MODAL_DONE_STATUS_VISIBILITY_TIME,
    UI_DECIMALS_DISPLAYED_ON_STEP_MODALS,
} from '../../../common/constants';
import { ConvertBalanceMustNotBeEqualException } from '../../../exceptions/convert_balance_must_not_be_equal_exception';
import { InsufficientEthDepositBalanceException } from '../../../exceptions/insufficient_eth_deposit_balance_exception';
import { UserDeniedTransactionSignatureException } from '../../../exceptions/user_denied_transaction_exception';
import {
    convertBalanceStateAsync,
    stepsModalAdvanceStep,
    updateTokenBalances,
    updateWethBalance,
} from '../../../store/actions';
import { getEstimatedTxTimeMs, getEthBalance, getStepsModalCurrentStep } from '../../../store/selectors';
import { getKnownTokens } from '../../../util/known_tokens';
import { sleep } from '../../../util/sleep';
import { tokenAmountInUnits, tokenAmountInUnitsToBigNumber } from '../../../util/tokens';
import { StepWrapEth, StoreState } from '../../../util/types';
import { isMobile } from "react-device-detect";

import { BaseStepModal } from './base_step_modal';
import { StepItem } from './steps_progress';
const I18n = require('i18n-js');

interface OwnProps {
    buildStepsProgress: (currentStepItem: StepItem) => StepItem[];
}
interface StateProps {
    estimatedTxTimeMs: number;
    step: StepWrapEth;
    ethBalance: BigNumber;
}

interface DispatchProps {
    updateWeth: (newWethBalance: BigNumber) => Promise<any>;
    updateTokenBalances: (txHash: string) => Promise<any>;
    convertBalanceState: { request: () => void; success: () => void; failure: () => void };
    advanceStep: () => void;
}

type Props = OwnProps & StateProps & DispatchProps;

interface State {
    errorCaption: string;
}

class WrapEthStep extends React.Component<Props, State> {
    public state = {
        errorCaption: '',
    };

    private txReceiptInterval: any | null;
    componentWillUnmount = () => {
        clearInterval(this.txReceiptInterval);
    };
    public render = () => {
        const { buildStepsProgress, estimatedTxTimeMs, step } = this.props;

        const { context, currentWethBalance, newWethBalance } = step;
        const amount = newWethBalance.minus(currentWethBalance);
        const wethToken = getKnownTokens().getWethToken();
        const ethAmount = tokenAmountInUnitsToBigNumber(amount.abs(), wethToken.decimals).toFixed(
            UI_DECIMALS_DISPLAYED_ON_STEP_MODALS,
        );

        const ethToWeth = amount.isGreaterThan(0);
        const convertingFrom = ethToWeth ? 'ETH' : 'wETH';
        const convertingTo = ethToWeth ? 'wETH' : 'ETH';

        const isOrder = context === 'order';
        const to = I18n.t('to')
        const buildMessage = (prefix: string) => {
            return [
                prefix,
                ethAmount,
                convertingFrom,
                isOrder ? I18n.t('forTrading') : null, // only show "for trading" when creating an order
                `(${convertingFrom} ${to} ${convertingTo}).`,
            ]
                .filter(x => x !== null)
                .join(' ');
        };

        let title = I18n.t('forTrading', { convertingFrom });
        if (this.state.errorCaption) {
            title = I18n.t('convertFailed');
        }
        const meta = isMobile ? 'Metamask' : '...';
        const confirmCaption = I18n.t('confirmOnMetamaskToConvert', { ethAmount, convertingFrom, convertingTo }).replace(meta, 'dApp');//`Confirm on Metamask to convert ${ethAmount} ${convertingFrom} into ${convertingTo}.`;
        const loadingCaption = buildMessage(I18n.t('converting'));
        const doneCaption = buildMessage(I18n.t('converted'));
        const loadingFooterCaption = I18n.t('waitingForConfirmation');
        const doneFooterCaption = I18n.t('convertedFrom', { convertingFrom });//`${convertingFrom} converted!`;

        return (
            <BaseStepModal
                step={step}
                title={title}
                confirmCaption={confirmCaption}
                loadingCaption={loadingCaption}
                doneCaption={doneCaption}
                errorCaption={this.state.errorCaption}
                loadingFooterCaption={loadingFooterCaption}
                doneFooterCaption={doneFooterCaption}
                buildStepsProgress={buildStepsProgress}
                estimatedTxTimeMs={estimatedTxTimeMs}
                runAction={this._convertWeth}
                showPartialProgress={true}
            />
        );
    };

    private readonly _convertWeth = async ({ onLoading, onDone, onError }: any) => {
        const { step, advanceStep, ethBalance, updateWeth, convertBalanceState } = this.props;
        const { currentWethBalance, newWethBalance } = step;
        const updateBalances = this.props.updateTokenBalances;
        const { web3 } = window;
        try {
            const convertTxHash = await updateWeth(newWethBalance);
            onLoading();
            this.txReceiptInterval = window.setInterval(async () => {
                await web3.eth.getTransactionReceipt(convertTxHash, async (error: any, result: any) => {
                    if (!error) {
                        if (result && result != null) {
                            clearInterval(this.txReceiptInterval);
                            convertBalanceState.request();
                            const balanceAfterUpdate = await updateBalances(convertTxHash) as BigNumber;
                            convertBalanceState.success();
                            onDone();
                            await sleep(STEP_MODAL_DONE_STATUS_VISIBILITY_TIME);
                            if (balanceAfterUpdate.isEqualTo(currentWethBalance)) {
                                await updateBalances(convertTxHash);
                                // alert('Not enough WEth balance. Please convert your Eth to WEth or wait for updating blockchain syncing!')
                            }
                            advanceStep();
                        }
                    } else {
                        console.error(error);
                    }
                })
            }, 3000);
            // time out clear interval if have no tx receipt.
            setTimeout(() => { clearInterval(this.txReceiptInterval); }, 60000);

        } catch (err) {
            let exception = err;
            let errorCaption = I18n.t('unexpectedErrorHappend');
            if (err.toString().includes(I18n.t('userDeniedTransactionSignature'))) {
                exception = new UserDeniedTransactionSignatureException();
                errorCaption = I18n.t('userDeniedTransactionSignature');
            } else if (err.toString().includes('INSUFFICIENT_ETH_BALANCE_FOR_DEPOSIT')) {
                const amount = currentWethBalance.isGreaterThanOrEqualTo(newWethBalance)
                    ? currentWethBalance.minus(newWethBalance)
                    : newWethBalance.minus(currentWethBalance);
                const currentEthAmount = tokenAmountInUnits(ethBalance, ETH_DECIMALS);
                const ethNeeded = tokenAmountInUnits(amount, ETH_DECIMALS);
                exception = new InsufficientEthDepositBalanceException(currentEthAmount, ethNeeded);
                errorCaption = I18n.t('YouHaveETHButNeedETH', { currentEthAmount, ethNeeded });//`You have ${currentEthAmount} ETH but you need ${ethNeeded} ETH to make this operation`;
            } else if (err instanceof ConvertBalanceMustNotBeEqualException) {
                exception = err;
                errorCaption = I18n.t('ErrorTryedWrapETH');
            }

            // Enable convert button: some conditions are dealt with exceptions and we don't
            // want to leave the button disabled
            convertBalanceState.success();

            this.setState({ errorCaption });
            onError(exception);
        }
    };
}

const mapStateToProps = (state: StoreState): StateProps => {
    return {
        estimatedTxTimeMs: getEstimatedTxTimeMs(state),
        step: getStepsModalCurrentStep(state) as StepWrapEth,
        ethBalance: getEthBalance(state),
    };
};

const WrapEthStepContainer = connect(
    mapStateToProps,
    (dispatch: any) => {
        return {
            updateWeth: (newWethBalance: BigNumber) => dispatch(updateWethBalance(newWethBalance)),
            updateTokenBalances: (txHash: string) => dispatch(updateTokenBalances(txHash)),
            advanceStep: () => dispatch(stepsModalAdvanceStep()),
            convertBalanceState: {
                request: () => dispatch(convertBalanceStateAsync.request()),
                success: () => dispatch(convertBalanceStateAsync.success()),
                failure: () => dispatch(convertBalanceStateAsync.failure()),
            },
        };
    },
)(WrapEthStep);

export { WrapEthStep, WrapEthStepContainer };
